import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth uses httpOnly cookies set by the backend — no tokens in localStorage.
export const apiClient = axios.create({ baseURL: API, withCredentials: true });

apiClient.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = err?.config?.url || "";
    // Whitelist endpoints where a 401 is an expected validation response
    // (wrong current-password, wrong login creds, session check) — do NOT
    // force-logout.
    const isExpected401 =
      url.endsWith("/auth/change-password") ||
      url.endsWith("/auth/login") ||
      url.endsWith("/auth/me") ||
      url.endsWith("/auth/sso/session");
    if (err?.response?.status === 401 && !isExpected401) {
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export const api = {
  login: (email, password) =>
    apiClient.post("/auth/login", { email, password }).then((r) => r.data),
  logout: () => apiClient.post("/auth/logout").then((r) => r.data),
  register: (body) => apiClient.post("/auth/register", body).then((r) => r.data),
  me: () => apiClient.get("/auth/me").then((r) => r.data),
  updateMe: (body) => apiClient.patch("/auth/me", body).then((r) => r.data),
  notificationsSchema: () =>
    apiClient.get("/notifications/schema").then((r) => r.data),
  inviteCustomer: (email, full_name = "", company_name = "", phone = "") =>
    apiClient
      .post("/customers/invite", { email, full_name, company_name, phone })
      .then((r) => r.data),
  changePassword: (current_password, new_password) =>
    apiClient
      .post("/auth/change-password", { current_password, new_password })
      .then((r) => r.data),
  forgotPassword: (email) =>
    apiClient.post("/auth/forgot-password", { email }).then((r) => r.data),
  verifyResetToken: (token) =>
    apiClient
      .get("/auth/forgot-password/verify", { params: { token } })
      .then((r) => r.data),
  confirmPasswordReset: (token, new_password) =>
    apiClient
      .post("/auth/forgot-password/confirm", { token, new_password })
      .then((r) => r.data),

  // Bids
  listBids: (params = {}) => apiClient.get("/bids", { params }).then((r) => r.data),
  createBid: (body) => apiClient.post("/bids", body).then((r) => r.data),
  updateBid: (id, body) =>
    apiClient.patch(`/bids/${id}`, body).then((r) => r.data),
  deleteBid: (id) => apiClient.delete(`/bids/${id}`).then((r) => r.data),
  bidAttachments: (id) =>
    apiClient.get(`/bids/${id}/attachments`).then((r) => r.data),

  // Company
  getCompany: () => apiClient.get("/company").then((r) => r.data),
  updateCompany: (body) =>
    apiClient.patch("/company", body).then((r) => r.data),
  uploadCompanyAsset: (formData) =>
    apiClient
      .post("/company/upload-asset", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data),

  // DMS
  listDmsDocs: (params = {}) =>
    apiClient.get("/dms/documents", { params }).then((r) => r.data),
  createDmsDoc: (body) =>
    apiClient.post("/dms/documents", body).then((r) => r.data),
  getDmsDoc: (id) => apiClient.get(`/dms/documents/${id}`).then((r) => r.data),
  updateDmsDoc: (id, body) =>
    apiClient.patch(`/dms/documents/${id}`, body).then((r) => r.data),
  deleteDmsDoc: (id) =>
    apiClient.delete(`/dms/documents/${id}`).then((r) => r.data),
  snoozeFollowup: (id, body) =>
    apiClient.post(`/dms/documents/${id}/snooze-followup`, body).then((r) => r.data),
  dmsClients: () =>
    apiClient.get("/dms/clients").then((r) => r.data),
  emailReceipt: (id, body = {}) =>
    apiClient.post(`/dms/documents/${id}/email-receipt`, body).then((r) => r.data),
  generateDmsPdf: (id) =>
    apiClient.post(`/dms/documents/${id}/generate-pdf`).then((r) => r.data),
  dmsPdfUrl: (id) => `${API}/dms/documents/${id}/pdf`,
  quotationFromProposal: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiClient
      .post("/dms/quotation-from-proposal", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      })
      .then((r) => r.data);
  },
  specAnalyze: (file, margin = 30) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("margin", margin);
    return apiClient
      .post("/dms/spec-analyze", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      })
      .then((r) => r.data);
  },
  quotationImportBatch: (files) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    return apiClient
      .post("/dms/quotation-import-batch", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      })
      .then((r) => r.data);
  },
  specAnalyzeStatus: (jobId) =>
    apiClient.get(`/dms/spec-analyze/${jobId}`).then((r) => r.data),
  quotationImport: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiClient
      .post("/dms/quotation-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      })
      .then((r) => r.data);
  },
  fxRates: () => apiClient.get("/dms/fx-rates").then((r) => r.data),
  priceBook: (q, limit = 8) =>
    apiClient.get("/dms/price-book", { params: { q, limit } }).then((r) => r.data),
  deletePriceBookEntry: (id) =>
    apiClient.delete(`/dms/price-book/${id}`).then((r) => r.data),
  hrOverview: () => apiClient.get("/hr/overview").then((r) => r.data),
  hrUpdateProfile: (userId, body) =>
    apiClient.patch(`/hr/profiles/${userId}`, body).then((r) => r.data),
  hrPayments: (params = {}) =>
    apiClient.get("/hr/payments", { params }).then((r) => r.data),
  hrCreatePayment: (body) =>
    apiClient.post("/hr/payments", body).then((r) => r.data),
  hrApprovePayment: (id, body = {}) =>
    apiClient.post(`/hr/payments/${id}/approve`, body).then((r) => r.data),
  hrRecoverAdvance: (id, amount) =>
    apiClient.post(`/hr/payments/${id}/recover`, { amount }).then((r) => r.data),
  hrDeletePayment: (id) =>
    apiClient.delete(`/hr/payments/${id}`).then((r) => r.data),
  hrSetAccess: (userId, hrAccess) =>
    apiClient.post(`/hr/access/${userId}`, { hr_access: hrAccess }).then((r) => r.data),
  hrPayslip: (id) =>
    apiClient.get(`/hr/payments/${id}/payslip`, { responseType: "blob" }),
  hrEmailPayslip: (id, to = "") =>
    apiClient.post(`/hr/payments/${id}/email-payslip`, { to }, { timeout: 60000 }).then((r) => r.data),
  myPayslips: () => apiClient.get("/hr/my-payslips").then((r) => r.data),
  myPayslipPdf: (id) =>
    apiClient.get(`/hr/my-payslips/${id}/pdf`, { responseType: "blob" }),
  addCashExpense: (body) =>
    apiClient.post("/documents/cash-expense", body).then((r) => r.data),
  hrFloats: () => apiClient.get("/hr/floats").then((r) => r.data),
  hrAddFloat: (body) => apiClient.post("/hr/floats", body).then((r) => r.data),
  hrDeleteFloat: (id) => apiClient.delete(`/hr/floats/${id}`).then((r) => r.data),
  hrCreateEmployee: (body) => apiClient.post("/hr/employees", body).then((r) => r.data),
  hrDeleteEmployee: (id) => apiClient.delete(`/hr/employees/${id}`).then((r) => r.data),
  hrEmployeeDocs: (id) => apiClient.get(`/hr/employees/${id}/documents`).then((r) => r.data),
  hrUploadEmployeeDoc: (id, file, label) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("label", label);
    return apiClient.post(`/hr/employees/${id}/documents`, fd, { timeout: 120000 }).then((r) => r.data);
  },
  hrDownloadEmployeeDoc: (docId) =>
    apiClient.get(`/hr/employee-docs/${docId}/download`, { responseType: "blob" }),
  hrDeleteEmployeeDoc: (docId) => apiClient.delete(`/hr/employee-docs/${docId}`).then((r) => r.data),
  listSuppliers: () => apiClient.get("/suppliers").then((r) => r.data),
  createSupplier: (body) => apiClient.post("/suppliers", body).then((r) => r.data),
  updateSupplier: (id, body) => apiClient.patch(`/suppliers/${id}`, body).then((r) => r.data),
  deleteSupplier: (id) => apiClient.delete(`/suppliers/${id}`).then((r) => r.data),
  supplierPrices: (id) => apiClient.get(`/suppliers/${id}/prices`).then((r) => r.data),
  supplierLpoItems: (id) => apiClient.get(`/suppliers/${id}/lpo-items`).then((r) => r.data),
  productSuppliers: (id) => apiClient.get(`/products/${id}/suppliers`).then((r) => r.data),
  approveLpo: (id) => apiClient.post(`/dms/documents/${id}/approve`).then((r) => r.data),
  bestPrice: (q) => apiClient.get("/dms/best-price", { params: { q } }).then((r) => r.data),
  emailDmsDoc: (id, body = {}) =>
    apiClient
      .post(`/dms/documents/${id}/email`, body, { timeout: 60000 })
      .then((r) => r.data),
  scanQuoteRequests: () =>
    apiClient
      .post("/dms/scan-quote-requests", null, { timeout: 180000 })
      .then((r) => r.data),
  listQuoteRequests: () =>
    apiClient.get("/dms/quote-requests").then((r) => r.data),
  listProducts: () => apiClient.get("/products").then((r) => r.data),
  createProduct: (body) => apiClient.post("/products", body).then((r) => r.data),
  updateProduct: (id, body) => apiClient.patch(`/products/${id}`, body).then((r) => r.data),
  deleteProduct: (id) => apiClient.delete(`/products/${id}`).then((r) => r.data),
  getPermissions: () => apiClient.get("/permissions").then((r) => r.data),
  updatePermissions: (body) => apiClient.patch("/permissions", body).then((r) => r.data),
  dmsEmailLog: (id) =>
    apiClient.get(`/dms/documents/${id}/email-log`).then((r) => r.data),

  // Tickets
  listTickets: (params = {}) => apiClient.get("/tickets", { params }).then((r) => r.data),
  createTicket: (body) => apiClient.post("/tickets", body).then((r) => r.data),
  getTicket: (id) => apiClient.get(`/tickets/${id}`).then((r) => r.data),
  updateTicket: (id, body) => apiClient.patch(`/tickets/${id}`, body).then((r) => r.data),
  deleteTicket: (id) => apiClient.delete(`/tickets/${id}`).then((r) => r.data),
  addTicketComment: (id, body) =>
    apiClient.post(`/tickets/${id}/comments`, body).then((r) => r.data),
  ticketsStats: () => apiClient.get("/tickets-stats/summary").then((r) => r.data),

  // Dashboard
  dashboard: () => apiClient.get("/dashboard/stats").then((r) => r.data),

  // Feeds
  feed: (src) => apiClient.get(`/feeds/${src}`).then((r) => r.data),
  refreshFeed: (src) => apiClient.post(`/feeds/${src}/refresh`).then((r) => r.data),
  feedsStatus: () => apiClient.get("/feeds/status").then((r) => r.data),

  // Backup (admin only)
  backupStatus: () => apiClient.get("/backup/status").then((r) => r.data),
  runBackup: () => apiClient.post("/backup/run-now").then((r) => r.data),
  downloadBackup: () =>
    apiClient.get("/backup/download", { responseType: "blob" }).then((r) => r),

  // WhatsApp (admin only)
  whatsappStatus: () => apiClient.get("/whatsapp/status").then((r) => r.data),
  whatsappTest: (body) =>
    apiClient.post("/whatsapp/test", body).then((r) => r.data),

  // Team
  team: () => apiClient.get("/team").then((r) => r.data),
  invite: (body) => apiClient.post("/team/invite", body).then((r) => r.data),
  updateMember: (id, body) =>
    apiClient.patch(`/team/${id}`, body).then((r) => r.data),
  deleteMember: (id) => apiClient.delete(`/team/${id}`).then((r) => r.data),
  resetMemberPassword: (id, opts = {}) =>
    apiClient
      .post(`/team/${id}/reset-password`, {
        temp_password: opts.temp_password || null,
        send_email: opts.send_email !== false,
      })
      .then((r) => r.data),

  // Docs
  listDocuments: (params = {}) =>
    apiClient.get("/documents", { params }).then((r) => r.data),
  uploadDocument: (formData) =>
    apiClient
      .post("/documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data),
  updateDocument: (id, body) =>
    apiClient.patch(`/documents/${id}`, body).then((r) => r.data),
  scanReceipt: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiClient
      .post("/documents/scan-receipt", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      })
      .then((r) => r.data);
  },
  exportExpenses: (year, month) =>
    apiClient.get("/documents/expense-export", {
      params: month ? { year, month } : { year },
      responseType: "blob",
    }),
  deleteDocument: (id) =>
    apiClient.delete(`/documents/${id}`).then((r) => r.data),
  downloadDocumentUrl: (id) => `${API}/documents/${id}/download`,
  expiringDocuments: (days = 60) =>
    apiClient.get(`/documents/expiring?days=${days}`).then((r) => r.data),

  // Bid Wizard
  extractBidFromText: (text, hint = "") => {
    const fd = new FormData();
    fd.append("text", text);
    if (hint) fd.append("hint", hint);
    return apiClient.post("/bid-wizard/extract", fd).then((r) => r.data);
  },
  extractBidFromPdf: (file, hint = "") => {
    const fd = new FormData();
    fd.append("file", file);
    if (hint) fd.append("hint", hint);
    return apiClient.post("/bid-wizard/extract", fd).then((r) => r.data);
  },
  extractBidFromUrl: (url, hint = "") =>
    apiClient
      .post("/bid-wizard/extract-from-url", { url, hint })
      .then((r) => r.data),
  createBidFromScrape: (source, item) =>
    apiClient
      .post("/bid-wizard/create-from-scrape", { source, item })
      .then((r) => r.data),
  compileBidPdf: (bidId) =>
    apiClient
      .post(`/bid-wizard/compile/${bidId}`, null, { responseType: "blob" })
      .then((r) => r),

  // Notifications
  listNotifications: () =>
    apiClient.get("/notifications").then((r) => r.data),
  markNotificationRead: (key) =>
    apiClient.post(`/notifications/${key}/read`).then((r) => r.data),

  // Customer portal — my documents
  listPortalDocuments: () =>
    apiClient.get("/portal/documents").then((r) => r.data),
  portalAcceptDoc: (id) =>
    apiClient.post(`/portal/documents/${id}/accept`).then((r) => r.data),
  portalRequestChanges: (id, comments) =>
    apiClient.post(`/portal/documents/${id}/request-changes`, { comments }).then((r) => r.data),
  portalUploadLpo: (id, file, lpoRef = "") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("lpo_ref", lpoRef);
    return apiClient
      .post(`/portal/documents/${id}/lpo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      })
      .then((r) => r.data);
  },

  // Google
  googleStatus: () => apiClient.get("/auth/google/status").then((r) => r.data),
  googleLogin: () => apiClient.get("/auth/google/login").then((r) => r.data),
  googleDisconnect: () =>
    apiClient.post("/auth/google/disconnect").then((r) => r.data),
  listGmail: (params = {}) =>
    apiClient.get("/gmail/messages", { params }).then((r) => r.data),
  getGmail: (id) => apiClient.get(`/gmail/messages/${id}`).then((r) => r.data),
  sendGmail: (form) => {
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => v != null && fd.append(k, v));
    return apiClient.post("/gmail/send", fd).then((r) => r.data);
  },
  listDrive: () => apiClient.get("/drive/files").then((r) => r.data),
  uploadDrive: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiClient.post("/drive/upload", fd).then((r) => r.data);
  },

  // Chat
  chat: (sessionId, message) =>
    apiClient
      .post("/chat/message", { session_id: sessionId, message })
      .then((r) => r.data),
  chatSessions: () => apiClient.get("/chat/sessions").then((r) => r.data),
  chatSession: (id) => apiClient.get(`/chat/sessions/${id}`).then((r) => r.data),
  deleteChatSession: (id) =>
    apiClient.delete(`/chat/sessions/${id}`).then((r) => r.data),

  // Proposals
  generateProposal: (formData) =>
    apiClient
      .post("/proposals/generate", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data),
  listProposals: () => apiClient.get("/proposals").then((r) => r.data),
  getProposal: (id) => apiClient.get(`/proposals/${id}`).then((r) => r.data),

  // Wizard-based bid proposal (BidForms_Standard template overlay)
  wizardDefaults: () =>
    apiClient.get("/proposal-wizard/defaults").then((r) => r.data),
  wizardCompile: (payload) =>
    apiClient
      .post("/proposal-wizard/compile", payload, { timeout: 300000 })
      .then((r) => r.data),
  wizardCompiledDownload: (proposalId) =>
    apiClient
      .get(`/proposal-wizard/compiled/${proposalId}`, { responseType: "blob", timeout: 300000 })
      .then((r) => r),
  wizardAiPrefill: ({ text, url, file }) => {
    const fd = new FormData();
    if (text) fd.append("text", text);
    if (url) fd.append("url", url);
    if (file) fd.append("file", file);
    return apiClient
      .post("/proposal-wizard/ai-prefill", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      })
      .then((r) => r.data);
  },
  // Drafts (save & resume)
  wizardListDrafts: () =>
    apiClient.get("/proposal-wizard/drafts").then((r) => r.data),
  wizardSaveDraft: (payload) =>
    apiClient.post("/proposal-wizard/drafts", payload).then((r) => r.data),
  wizardLoadDraft: (id) =>
    apiClient.get(`/proposal-wizard/drafts/${id}`).then((r) => r.data),
  wizardDeleteDraft: (id) =>
    apiClient.delete(`/proposal-wizard/drafts/${id}`).then((r) => r.data),
};
